/* screens/Dashboard/Overview/RightAsidePanel.tsx
   -------------------------------------------------------------- */
import React, {useEffect, useMemo, useState} from 'react';
import {CircularProgress, Collapse, Typography} from '@material-ui/core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faClipboardList,
  faFileInvoice,
  faUserPlus,
} from '@fortawesome/pro-light-svg-icons';
import {Link} from 'react-router-dom';

import {reduxToggleSidePanel} from 'appredux/actions';
import {
  apiGetAllFdApplications,
  apiGetDepotOrders,
  apiGetDepotQuotes,
  apiGetInputPrograms,
  apiGetSupportCustomers,
} from 'constants/Api';
import {EApplicationStateType, ESidePanelView} from 'constants/index';
import {
  IFarmInputInvoice,
  IFarmInputProgram,
  IFarmInputQuote,
  IFarmer,
  IFdApplicationType,
} from 'constants/interfaces';
import {
  lshFilterFdApplicationsForLinkedCustomers,
  lshFilterInputProgramsForLinkedCustomers,
} from 'services/SupplierCustomerScope';

import './RightAsidePanel.scss';

type AttentionItem = {
  id: string;
  count: number;
  label: string;
  meta: string;
  to: string;
};

type ActivityItem = {
  id: string;
  title: string;
  meta: string;
  date: Date;
  to: string;
};

type PanelData = {
  applications: IFdApplicationType[];
  orders: IFarmInputInvoice[];
  quotes: IFarmInputQuote[];
  programs: IFarmInputProgram[];
};

type SectionKey = 'attention' | 'activity';

const EMPTY_DATA: PanelData = {
  applications: [],
  orders: [],
  quotes: [],
  programs: [],
};

function parseRecordDate(value?: string | number | null): Date | null {
  if (value === null || value === undefined || value === '') return null;

  const text = String(value);
  const numericValue = Number(text);
  const isNumericDate = Number.isFinite(numericValue) && /^\d+$/.test(text);
  const date = isNumericDate
    ? new Date(numericValue < 100000000000 ? numericValue * 1000 : numericValue)
    : new Date(text);

  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatRelativeDate(date: Date): string {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfDate.getTime()) / 86400000,
  );

  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return `${dayDiff} days ago`;

  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
  });
}

function farmerName(farmer?: IFarmer): string {
  return [farmer?.name, farmer?.surname].filter(Boolean).join(' ').trim();
}

function applicationName(application: IFdApplicationType): string {
  return (
    application.company?.name ||
    application.farm?.name ||
    [application.applyingUser?.name, application.applyingUser?.surname]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    `Application #${application.id}`
  );
}

function programName(program: IFarmInputProgram): string {
  return (
    program.produce?.name ||
    program.farmer?.farm?.farmName ||
    program.farmer?.farm?.name ||
    `Program #${program.id}`
  );
}

function orderLabel(order: IFarmInputInvoice): string {
  return order.invoiceNumber
    ? `Order ${order.invoiceNumber}`
    : `Order #${order.id}`;
}

function quoteLabel(quote: IFarmInputQuote): string {
  return quote.quoteNumber
    ? `Quote ${quote.quoteNumber}`
    : `Quote #${quote.id}`;
}

function buildAttentionItems(data: PanelData): AttentionItem[] {
  const pendingReview = data.applications.filter(
    (application) =>
      application.applicationState === EApplicationStateType.PENDING_REVIEW ||
      application.applicationState === EApplicationStateType.IN_REVIEW,
  ).length;
  const incompleteApplications = data.applications.filter(
    (application) => (application.incompleteSections || []).length > 0,
  ).length;
  const signatureApplications = data.applications.filter((application) =>
    [
      EApplicationStateType.PENDING_SIGNATURES,
      EApplicationStateType.TERM_SHEET_SIGNATURE,
      EApplicationStateType.CONDITION_PRECEDENT,
    ].includes(application.applicationState),
  ).length;
  const unpaidOrders = data.orders.filter(
    (order) => !order.isPaid || Number(order.outstanding || 0) > 0,
  ).length;
  const expiredQuotes = data.quotes.filter((quote) => quote.isExpired).length;
  const programsWithoutProducts = data.programs.filter(
    (program) => !(program.lines || []).length,
  ).length;

  return [
    {
      id: 'review',
      count: pendingReview,
      label: 'Funding reviews',
      meta: 'Ready for supplier review',
      to: '/applications',
    },
    {
      id: 'missing',
      count: incompleteApplications,
      label: 'Incomplete applications',
      meta: 'Missing application sections',
      to: '/applications',
    },
    {
      id: 'signature',
      count: signatureApplications,
      label: 'Signature follow-ups',
      meta: 'Waiting on terms or conditions',
      to: '/applications',
    },
    {
      id: 'unpaid',
      count: unpaidOrders,
      label: 'Unpaid orders',
      meta: 'Payment still outstanding',
      to: '/orders',
    },
    {
      id: 'quotes',
      count: expiredQuotes,
      label: 'Expired quotes',
      meta: 'Follow up or recreate',
      to: '/quotes',
    },
    {
      id: 'programs',
      count: programsWithoutProducts,
      label: 'Programs without products',
      meta: 'Add product lines',
      to: '/programs',
    },
  ].filter((item) => item.count > 0);
}

function buildActivityItems(data: PanelData): ActivityItem[] {
  const applicationItems = data.applications.map((application) => ({
    id: `application-${application.id}`,
    title: 'Funding application',
    meta: applicationName(application),
    date: parseRecordDate(application.createdAt) || new Date(0),
    to: `/applications/${application.id}`,
  }));

  const orderItems = data.orders.map((order) => ({
    id: `order-${order.id}`,
    title: orderLabel(order),
    meta: farmerName(order.farmer) || order.farmer?.farm?.name || 'Order',
    date:
      parseRecordDate(order.updatedDate) ||
      parseRecordDate(order.insertedDate) ||
      new Date(0),
    to: `/orders/${order.id}`,
  }));

  const quoteItems = data.quotes.map((quote) => ({
    id: `quote-${quote.id}`,
    title: quoteLabel(quote),
    meta: quote.formattedState || quote.state || farmerName(quote.farmer),
    date:
      parseRecordDate((quote as any).updateDate) ||
      parseRecordDate(quote.insertedDate) ||
      parseRecordDate((quote as any).insertedOn) ||
      new Date(0),
    to: `/quotes/${quote.id}`,
  }));

  const programItems = data.programs.map((program) => ({
    id: `program-${program.id}`,
    title: 'Input program',
    meta: programName(program),
    date:
      parseRecordDate(program.updatedDate) ||
      parseRecordDate(program.createdDate) ||
      new Date(0),
    to: `/programs/${program.id}`,
  }));

  return [...applicationItems, ...orderItems, ...quoteItems, ...programItems]
    .filter((item) => item.date.getTime() > 0)
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, 5);
}

export default function RightAsidePanel(): React.ReactElement {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [data, setData] = useState<PanelData>(EMPTY_DATA);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(
    {
      attention: true,
      activity: false,
    },
  );

  useEffect(() => {
    let alive = true;

    Promise.all([
      apiGetAllFdApplications().catch((error) => {
        console.error('Failed to load action centre applications', error);
        setHasError(true);
        return [];
      }),
      apiGetDepotOrders(20).catch((error) => {
        console.error('Failed to load action centre orders', error);
        setHasError(true);
        return [];
      }),
      apiGetDepotQuotes(20).catch((error) => {
        console.error('Failed to load action centre quotes', error);
        setHasError(true);
        return [];
      }),
      apiGetInputPrograms(50).catch((error) => {
        console.error('Failed to load action centre programs', error);
        setHasError(true);
        return [];
      }),
      apiGetSupportCustomers().catch((error) => {
        console.error('Failed to load action centre customers', error);
        setHasError(true);
        return [];
      }),
    ])
      .then(([applications, orders, quotes, programs, customers]) => {
        if (!alive) return;
        setData({
          applications: lshFilterFdApplicationsForLinkedCustomers(
            applications,
            customers,
          ),
          orders,
          quotes,
          programs: lshFilterInputProgramsForLinkedCustomers(
            programs,
            customers,
          ),
        });
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const attentionItems = useMemo(() => buildAttentionItems(data), [data]);
  const activityItems = useMemo(() => buildActivityItems(data), [data]);

  const openProgramPanel = () =>
    reduxToggleSidePanel(
      true,
      {panelType: ESidePanelView.ADD_PROGRAM, data: null},
      false,
    );

  const openTeamPanel = () =>
    reduxToggleSidePanel(
      true,
      {panelType: ESidePanelView.ADD_TEAM_MEMBER, data: {}},
      false,
    );

  const toggleSection = (section: SectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <div className="aside-wrapper action-centre">
      <section className="action-card action-card-primary">
        <Typography className="action-eyebrow">Action centre</Typography>

        <div className="collapsible-section">
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection('attention')}
          >
            <span>
              <span className="section-title">Needs attention</span>
              <span className="section-subtitle">
                {isLoading
                  ? 'Checking work queue'
                  : `${attentionItems.length} follow-up groups`}
              </span>
            </span>
            <span className="section-toggle-end">
              {isLoading ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                <span className="section-count">{attentionItems.length}</span>
              )}
              <FontAwesomeIcon
                icon={faChevronDown}
                className={openSections.attention ? 'is-open' : ''}
              />
            </span>
          </button>

          <Collapse in={openSections.attention}>
            <div className="section-body">
              {!isLoading && attentionItems.length === 0 && (
                <Typography className="empty-state">
                  No urgent follow-ups right now.
                </Typography>
              )}

              {!isLoading &&
                attentionItems.slice(0, 4).map((item) => (
                  <Link key={item.id} to={item.to} className="attention-row">
                    <span className="attention-count">{item.count}</span>
                    <span className="attention-copy">
                      <span className="attention-label">{item.label}</span>
                      <span className="attention-meta">{item.meta}</span>
                    </span>
                  </Link>
                ))}

              {hasError && !isLoading && (
                <Typography className="action-error">
                  Some dashboard items could not load.
                </Typography>
              )}
            </div>
          </Collapse>
        </div>

        <div className="collapsible-section">
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection('activity')}
          >
            <span>
              <span className="section-title">Recent activity</span>
              <span className="section-subtitle">
                Latest applications, orders and quotes
              </span>
            </span>
            <span className="section-toggle-end">
              <span className="section-count">{activityItems.length}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={openSections.activity ? 'is-open' : ''}
              />
            </span>
          </button>

          <Collapse in={openSections.activity}>
            <div className="section-body">
              <Link to="/applications" className="section-link">
                View all
              </Link>

              {isLoading && (
                <Typography className="empty-state">
                  Loading activity...
                </Typography>
              )}

              {!isLoading && activityItems.length === 0 && (
                <Typography className="empty-state">
                  No recent activity yet.
                </Typography>
              )}

              {!isLoading &&
                activityItems.map((item) => (
                  <Link key={item.id} to={item.to} className="activity-row">
                    <span className="activity-dot" />
                    <span className="activity-copy">
                      <span className="activity-title">{item.title}</span>
                      <span className="activity-meta">
                        {item.meta} • {formatRelativeDate(item.date)}
                      </span>
                    </span>
                  </Link>
                ))}
            </div>
          </Collapse>
        </div>

        <nav className="quick-tiles">
          <button type="button" className="tile" onClick={openProgramPanel}>
            <span className="tile-icon">
              <FontAwesomeIcon icon={faClipboardList} />
            </span>
            <span className="tile-text">Add program</span>
          </button>

          <Link to="/create-quote" className="tile">
            <span className="tile-icon">
              <FontAwesomeIcon icon={faFileInvoice} />
            </span>
            <span className="tile-text">Create quote</span>
          </Link>

          <button type="button" className="tile" onClick={openTeamPanel}>
            <span className="tile-icon">
              <FontAwesomeIcon icon={faUserPlus} />
            </span>
            <span className="tile-text">Add team</span>
          </button>
        </nav>
      </section>
    </div>
  );
}
